/**
 * SVG path definitions for dental tooth shapes — labial (frontal) view.
 * 5 tooth types x 3 variants (natural, rounded, square).
 *
 * Modeled from dental anatomy with:
 *   - Deep gingival scallop with proper zenith positioning
 *   - Smooth continuous curves (all C/Q — no straight L segments)
 *   - Correct mesial/distal asymmetry (mesial straighter, distal more curved)
 *   - Proper taper: cervical narrower → incisal wider
 *   - Canines with genuine pointed cusps
 *   - Anatomical proportions (H:W ≈ 1.2 for centrals, 1.7 for canines)
 */

export const TOOTH_SHAPES = {
  // ──────────── CENTRAL INCISOR ────────────
  // Widest anterior tooth. Nearly rectangular with natural curves.
  // Gingival zenith slightly distal of center.
  centralIncisor: {
    natural: {
      path: [
        'M 24 20',
        'C 34 4, 46 2, 54 2',       // gingival → zenith (distal of center)
        'C 62 2, 72 4, 78 20',       // zenith → distal interproximal
        'C 82 32, 88 52, 92 80',     // distal cervical & body
        'C 94 96, 90 112, 80 120',   // distal-incisal (well rounded)
        'C 70 126, 60 128, 50 128',  // incisal edge distal half
        'C 40 128, 30 126, 20 120',  // incisal edge mesial half
        'C 10 112, 6 96, 8 80',      // mesial-incisal (sharper)
        'C 10 52, 16 32, 24 20',     // mesial body & cervical
        'Z',
      ].join(' '),
      width: 100,
      height: 130,
      details: [
        { path: 'M 38 22 C 36 52, 36 82, 37 112', type: 'stroke' },
        { path: 'M 62 22 C 64 52, 64 82, 63 112', type: 'stroke' },
      ],
    },
    rounded: {
      path: [
        'M 26 20',
        'C 34 6, 44 2, 50 2',
        'C 58 2, 68 6, 74 20',
        'C 80 34, 86 54, 90 78',
        'C 92 96, 86 114, 72 124',
        'C 62 130, 54 132, 50 132',
        'C 46 132, 38 130, 28 124',
        'C 14 114, 8 96, 10 78',
        'C 14 54, 20 34, 26 20',
        'Z',
      ].join(' '),
      width: 100,
      height: 134,
      details: [
        { path: 'M 38 20 C 37 52, 37 82, 38 114', type: 'stroke' },
        { path: 'M 62 20 C 63 52, 63 82, 62 114', type: 'stroke' },
      ],
    },
    square: {
      path: [
        'M 22 18',
        'C 30 4, 44 2, 52 2',
        'C 60 2, 74 4, 80 18',
        'C 84 28, 88 44, 90 64',
        'C 92 84, 92 100, 90 110',
        'C 88 118, 84 122, 78 124',
        'C 66 126, 56 126, 50 126',
        'C 44 126, 34 126, 22 124',
        'C 16 122, 12 118, 10 110',
        'C 8 100, 8 84, 10 64',
        'C 12 44, 16 28, 22 18',
        'Z',
      ].join(' '),
      width: 100,
      height: 128,
      details: [
        { path: 'M 36 18 C 36 48, 36 80, 36 110', type: 'stroke' },
        { path: 'M 64 18 C 64 48, 64 80, 64 110', type: 'stroke' },
      ],
    },
  },

  // ──────────── LATERAL INCISOR ────────────
  // Smaller than central, more rounded overall.
  // Distal-incisal angle noticeably more rounded.
  lateralIncisor: {
    natural: {
      path: [
        'M 18 18',
        'C 26 4, 34 2, 40 2',       // gingival → zenith
        'C 48 2, 56 6, 60 18',       // zenith → distal
        'C 64 30, 68 48, 70 68',     // distal body
        'C 72 84, 68 98, 58 106',    // distal-incisal (very rounded)
        'C 50 112, 42 114, 38 114',  // incisal distal half
        'C 32 114, 24 112, 16 106',  // incisal mesial half
        'C 8 98, 4 84, 6 68',        // mesial-incisal (sharper)
        'C 8 48, 12 30, 18 18',      // mesial body
        'Z',
      ].join(' '),
      width: 76,
      height: 116,
      details: [
        { path: 'M 28 18 C 27 48, 27 72, 28 100', type: 'stroke' },
        { path: 'M 48 18 C 49 48, 49 72, 48 100', type: 'stroke' },
      ],
    },
    rounded: {
      path: [
        'M 20 18',
        'C 28 4, 36 2, 38 2',
        'C 42 2, 52 4, 58 18',
        'C 64 32, 68 52, 70 72',
        'C 72 88, 64 104, 50 112',
        'C 42 118, 38 118, 36 118',
        'C 32 118, 24 116, 16 108',
        'C 6 98, 4 84, 6 68',
        'C 8 48, 14 30, 20 18',
        'Z',
      ].join(' '),
      width: 76,
      height: 120,
      details: [
        { path: 'M 28 16 C 27 46, 27 70, 28 98', type: 'stroke' },
        { path: 'M 48 16 C 49 46, 49 70, 48 98', type: 'stroke' },
      ],
    },
    square: {
      path: [
        'M 16 16',
        'C 24 4, 34 2, 40 2',
        'C 48 2, 58 4, 62 16',
        'C 66 26, 68 42, 70 62',
        'C 72 80, 70 96, 62 106',
        'C 56 112, 46 114, 38 114',
        'C 30 114, 20 112, 14 106',
        'C 6 96, 4 80, 6 62',
        'C 8 42, 10 26, 16 16',
        'Z',
      ].join(' '),
      width: 76,
      height: 116,
      details: [
        { path: 'M 28 14 C 28 44, 28 68, 28 98', type: 'stroke' },
        { path: 'M 48 14 C 48 44, 48 68, 48 98', type: 'stroke' },
      ],
    },
  },

  // ──────────── CANINE ────────────
  // Diamond/pentagonal outline with prominent pointed cusp.
  // Mesial cusp slope shorter than distal. Strong labial ridge.
  canine: {
    natural: {
      path: [
        'M 16 18',
        'C 22 4, 32 2, 38 2',       // gingival → zenith (distal of center)
        'C 46 2, 54 6, 58 18',       // zenith → distal
        'C 62 30, 66 48, 66 66',     // distal upper body
        'C 66 82, 62 96, 54 108',    // distal lower body → cusp slope
        'C 46 118, 38 128, 34 132',  // cusp tip
        'C 30 128, 22 118, 16 108',  // mesial cusp slope
        'C 8 96, 4 82, 4 66',        // mesial lower body
        'C 4 48, 8 30, 16 18',       // mesial upper body
        'Z',
      ].join(' '),
      width: 70,
      height: 134,
      details: [
        { path: 'M 34 16 C 34 48, 34 80, 34 114', type: 'stroke' },
      ],
    },
    rounded: {
      path: [
        'M 18 18',
        'C 24 6, 32 2, 36 2',
        'C 42 2, 50 6, 56 18',
        'C 62 32, 66 52, 66 70',
        'C 66 88, 60 104, 50 116',
        'C 42 126, 38 130, 34 130',
        'C 30 130, 26 126, 20 116',
        'C 10 104, 4 88, 4 70',
        'C 4 52, 10 32, 18 18',
        'Z',
      ].join(' '),
      width: 70,
      height: 132,
      details: [
        { path: 'M 34 16 C 34 48, 34 78, 34 112', type: 'stroke' },
      ],
    },
    square: {
      path: [
        'M 14 16',
        'C 22 4, 30 2, 36 2',
        'C 44 2, 52 4, 56 16',
        'C 62 28, 64 46, 66 66',
        'C 66 82, 64 96, 56 108',
        'C 48 118, 40 128, 34 130',
        'C 28 128, 20 118, 14 108',
        'C 6 96, 4 82, 4 66',
        'C 4 46, 8 28, 14 16',
        'Z',
      ].join(' '),
      width: 70,
      height: 132,
      details: [
        { path: 'M 34 14 C 34 46, 34 78, 34 112', type: 'stroke' },
      ],
    },
  },

  // ──────────── FIRST PREMOLAR ────────────
  // Shorter crown. Single buccal cusp visible from frontal view.
  firstPremolar: {
    natural: {
      path: [
        'M 14 14',
        'C 22 4, 30 2, 36 2',
        'C 44 2, 52 4, 58 14',
        'C 62 24, 66 38, 68 56',
        'C 70 70, 66 82, 58 90',
        'C 50 96, 42 100, 36 100',
        'C 28 100, 20 96, 14 90',
        'C 6 82, 2 70, 4 56',
        'C 6 38, 10 24, 14 14',
        'Z',
      ].join(' '),
      width: 72,
      height: 102,
      details: [
        { path: 'M 36 12 C 36 38, 36 60, 36 84', type: 'stroke' },
      ],
    },
    rounded: {
      path: [
        'M 16 14',
        'C 22 4, 32 2, 36 2',
        'C 42 2, 50 4, 56 14',
        'C 62 26, 66 44, 68 62',
        'C 70 78, 62 92, 48 100',
        'C 42 104, 38 104, 36 104',
        'C 32 104, 26 102, 20 96',
        'C 8 86, 2 72, 4 56',
        'C 6 38, 10 24, 16 14',
        'Z',
      ].join(' '),
      width: 72,
      height: 106,
      details: [
        { path: 'M 36 12 C 36 38, 36 58, 36 84', type: 'stroke' },
      ],
    },
    square: {
      path: [
        'M 12 12',
        'C 20 4, 30 2, 36 2',
        'C 44 2, 54 4, 60 12',
        'C 64 22, 68 38, 68 56',
        'C 68 72, 66 84, 58 90',
        'C 50 96, 42 98, 36 98',
        'C 28 98, 20 96, 14 90',
        'C 6 84, 4 72, 4 56',
        'C 4 38, 8 22, 12 12',
        'Z',
      ].join(' '),
      width: 72,
      height: 100,
      details: [
        { path: 'M 36 10 C 36 36, 36 58, 36 82', type: 'stroke' },
      ],
    },
  },

  // ──────────── SECOND PREMOLAR ────────────
  // Similar to first premolar, slightly smaller and more rounded.
  secondPremolar: {
    natural: {
      path: [
        'M 12 14',
        'C 18 4, 28 2, 32 2',
        'C 38 2, 48 4, 52 14',
        'C 56 24, 60 38, 62 52',
        'C 64 66, 60 78, 52 86',
        'C 44 92, 38 94, 32 94',
        'C 26 94, 20 92, 12 86',
        'C 4 78, 0 66, 2 52',
        'C 4 38, 8 24, 12 14',
        'Z',
      ].join(' '),
      width: 66,
      height: 96,
      details: [
        { path: 'M 32 12 C 32 36, 32 56, 32 78', type: 'stroke' },
      ],
    },
    rounded: {
      path: [
        'M 14 14',
        'C 20 4, 28 2, 32 2',
        'C 38 2, 46 4, 50 14',
        'C 56 26, 60 42, 62 58',
        'C 64 74, 56 88, 44 94',
        'C 38 98, 34 98, 32 98',
        'C 28 98, 22 96, 16 90',
        'C 6 82, 0 68, 2 52',
        'C 4 36, 8 24, 14 14',
        'Z',
      ].join(' '),
      width: 66,
      height: 100,
      details: [
        { path: 'M 32 12 C 32 36, 32 54, 32 78', type: 'stroke' },
      ],
    },
    square: {
      path: [
        'M 10 12',
        'C 16 4, 26 2, 32 2',
        'C 40 2, 48 4, 54 12',
        'C 58 22, 62 38, 62 54',
        'C 62 68, 58 80, 52 86',
        'C 44 92, 38 94, 32 94',
        'C 26 94, 20 92, 12 86',
        'C 6 80, 2 68, 2 54',
        'C 2 38, 6 22, 10 12',
        'Z',
      ].join(' '),
      width: 66,
      height: 96,
      details: [
        { path: 'M 32 10 C 32 34, 32 54, 32 78', type: 'stroke' },
      ],
    },
  },
};

// Tooth type display names
export const TOOTH_TYPE_NAMES = {
  centralIncisor: 'Central Incisor',
  lateralIncisor: 'Lateral Incisor',
  canine: 'Canine',
  firstPremolar: '1st Premolar',
  secondPremolar: '2nd Premolar',
};

// Shape variant display names
export const SHAPE_VARIANT_NAMES = {
  natural: 'Natural',
  rounded: 'Rounded',
  square: 'Square',
};

// Default tooth order from midline outward (per side)
export const TOOTH_ORDER = [
  'centralIncisor',
  'lateralIncisor',
  'canine',
  'firstPremolar',
  'secondPremolar',
];

/**
 * Get the shape data for a specific tooth type and variant.
 */
export const getToothShape = (toothType, variant = 'natural') => {
  const typeShapes = TOOTH_SHAPES[toothType];
  if (!typeShapes) return null;
  return typeShapes[variant] || typeShapes.natural;
};

/**
 * Get all available variants for a tooth type.
 */
export const getToothVariants = (toothType) => {
  const typeShapes = TOOTH_SHAPES[toothType];
  if (!typeShapes) return [];
  return Object.keys(typeShapes);
};
